<template>
  <section class="features-section" aria-labelledby="features-title">
    <div class="features-container">
    <div class="heading2">
        <h2 id="features-title" class="features-and-modules">Features and Modules</h2>
      </div>

      <div class="features-tablist" role="tablist" aria-label="Module categories">
        <se-button
          v-for="(module, index) in moduleCategories"
          :key="module.id"
          :id="`module-tab-${module.id}`"
          type="button"
          variant="text"
          style-set="set8"
          class="component-tab"
          :class="{ active: activeModuleTab === index }"
          role="tab"
          :aria-selected="activeModuleTab === index"
          :aria-controls="`module-panel-${module.id}`"
          :tabindex="activeModuleTab === index ? 0 : -1"
          @click="setActiveModuleTab(index)"
          @keydown="handleTabKeydown($event, index)"
        >
          <span class="tab-text">{{ module.name }}</span>
        </se-button>
      </div>

      <div
        :id="`module-panel-${activeModuleData.id}`"
        class="features-tabpanel"
        role="tabpanel"
        :aria-labelledby="`module-tab-${activeModuleData.id}`"
      >
        <div class="features-content">
          <div class="specialized-heading">
            <b class="section-title-text">{{ activeModuleData.title }}</b>
          </div>
          <div class="features-tools">
            <se-button
              v-for="tool in activeModuleData.tools"
              :key="tool.name"
              type="button"
              variant="text"
              style-set="set8"
              class="features-tool"
              :to="tool.route"
            >
              <div class="tool-icon">
                <s-e-icon v-if="tool.seIcon" :name="tool.seIcon" :size="20" />
                <v-icon v-else :icon="tool.icon" size="20" color="primary" />
              </div>
              <div class="tool-text">{{ tool.name }}</div>
            </se-button>
          </div>
        </div>

        <div class="module-preview-container">
          <!-- Single image - show directly -->
          <div v-if="activePreviewImages.length === 1" class="preview-single">
            <img
              :src="activePreviewImages[0].src"
              :alt="activePreviewImages[0].alt"
              class="module-preview-image"
              loading="lazy"
            />
          </div>

          <!-- Multiple images - carousel with fade transition -->
          <div v-else-if="activePreviewImages.length > 1" class="preview-carousel">
            <transition name="fade" mode="out-in">
              <img
                :key="currentImageIndex"
                :src="activePreviewImages[currentImageIndex].src"
                :alt="activePreviewImages[currentImageIndex].alt"
                class="module-preview-image"
                loading="lazy"
              />
            </transition>
          </div>

          <!-- No images - placeholder -->
          <div v-else class="preview-placeholder">
            <div class="placeholder-icon">📊</div>
            <p>Interactive Preview</p>
            <p class="placeholder-subtitle">Coming Soon</p>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref, computed, watch, onUnmounted } from 'vue'
import { useTheme } from 'vuetify'
import { calculatorsMeta, type CalculatorDomain } from '@/data/calculators'
import { calculators as calculatorDirectory } from '@/data/calculatorDirectory'
import SEIcon from '@/components/ui/SEIcon.vue'
import SeButton from '@/components/SEButton.vue'

// Homepage preview images - dark theme
import concreteBeamDark from '@/assets/images/homepage/concrete beam design - dark theme.png'
import purlinDesignDark from '@/assets/images/homepage/purlin design -dark theme.png'

// Homepage preview images - light theme
import concreteBeamLight from '@/assets/images/homepage/concrete beam design - light theme.png'
import purlinDesignLight from '@/assets/images/homepage/purlin design -light theme.png'

// Theme
const theme = useTheme()

// Local state
const activeModuleTab = ref(0)
const currentImageIndex = ref(0)
let carouselInterval: ReturnType<typeof setInterval> | null = null

// Icon lookup: normalize directory IDs to underscore-separated slugs
const iconMap = computed<Record<string, string>>(() => {
  const map: Record<string, string> = {}
  calculatorDirectory.forEach(c => { map[c.id.replace(/-/g, '_')] = c.icon })
  return map
})

// MDI overrides for slugs that don't match normalized directory IDs
const mdiOverrideMap: Record<string, string> = {
  'is_code_lookup': 'mdi-table-search',
  'load_combination_generator': 'mdi-sigma',
}

// Custom structural SE icon names keyed by calculator slug (underscore form)
const seIconMap: Record<string, string> = {
  conc_beam_design: 'concrete-beam',
  column_design: 'rc-column',
  slab_design: 'slab',
  crack_width: 'crack-width',
  footing_design: 'footing',
  retaining_wall: 'retaining-wall',
  base_plate: 'base-plate',
  steel_column: 'steel-column',
  purlin_design: 'purlin',
  gantry_girder: 'gantry',
}

// Module categories
const moduleCategories = [
  { id: 'concrete' as CalculatorDomain, name: 'Concrete' },
  { id: 'steel' as CalculatorDomain, name: 'Steel' },
  { id: 'specialized' as CalculatorDomain, name: 'Specialized' }
]

// Domain titles
const titles: Record<string, string> = {
  concrete: 'Concrete Design Tools',
  steel: 'Structural Steel Design',
  specialized: 'Specialized Tools'
}

// Computed
const activeModuleData = computed(() => {
  const activeModule = moduleCategories[activeModuleTab.value]
  const domain = activeModule.id

  if (domain === 'specialized') {
    return {
      id: activeModule.id,
      title: 'Specialized Tools',
      tools: calculatorsMeta
        .filter(c => c.domain === 'specialized' && (c.stage === 'live' || c.stage === 'beta'))
        .map(c => ({
          name: c.name,
          route: c.route || '/calculators',
          icon: mdiOverrideMap[c.slug.replace(/-/g, '_')] || iconMap.value[c.slug.replace(/-/g, '_')] || 'mdi-calculator-variant',
          seIcon: seIconMap[c.slug.replace(/-/g, '_')] || ''
        }))
    }
  }

  const tools = calculatorsMeta
    .filter(c => c.domain === domain && (c.stage === 'live' || c.stage === 'beta'))
    .map(c => ({
      name: c.name,
      route: c.route || '/calculators',
      icon: iconMap.value[c.slug] || 'mdi-calculator-variant',
      seIcon: seIconMap[c.slug] || ''
    }))

  return {
    id: activeModule.id,
    title: titles[domain] || activeModule.name,
    tools
  }
})

// Preview image interface
interface PreviewImage {
  src: string
  alt: string
}

// Computed property for theme-aware preview images
const activePreviewImages = computed<PreviewImage[]>(() => {
  const activeModule = moduleCategories[activeModuleTab.value]
  const isDark = theme.global.current.value.dark

  const imageMap: Record<string, PreviewImage[]> = {
    concrete: isDark
      ? [{ src: concreteBeamDark, alt: 'Concrete Beam Design Preview' }]
      : [{ src: concreteBeamLight, alt: 'Concrete Beam Design Preview' }],
    steel: isDark
      ? [{ src: purlinDesignDark, alt: 'Purlin Design Preview' }]
      : [{ src: purlinDesignLight, alt: 'Purlin Design Preview' }],
    specialized: []
  }

  return imageMap[activeModule.id] || []
})

// Carousel auto-rotation
const startCarousel = () => {
  stopCarousel()
  if (activePreviewImages.value.length > 1) {
    carouselInterval = setInterval(() => {
      currentImageIndex.value = (currentImageIndex.value + 1) % activePreviewImages.value.length
    }, 5000)
  }
}

const stopCarousel = () => {
  if (carouselInterval) {
    clearInterval(carouselInterval)
    carouselInterval = null
  }
}

// Watch for tab changes to reset and restart carousel
watch(activeModuleTab, () => {
  currentImageIndex.value = 0
  startCarousel()
})

// Watch for image list changes (e.g., theme change)
watch(activePreviewImages, () => {
  if (currentImageIndex.value >= activePreviewImages.value.length) {
    currentImageIndex.value = 0
  }
  startCarousel()
}, { immediate: true })

// Cleanup on unmount
onUnmounted(() => {
  stopCarousel()
})

// Methods
const setActiveModuleTab = (index: number) => {
  activeModuleTab.value = index
}

const handleTabKeydown = (event: KeyboardEvent, index: number) => {
  const lastIndex = moduleCategories.length - 1
  const nextIndex = event.key === 'ArrowRight'
    ? (index + 1) % moduleCategories.length
    : event.key === 'ArrowLeft'
      ? (index - 1 + moduleCategories.length) % moduleCategories.length
      : event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? lastIndex
          : null

  if (nextIndex === null) return

  event.preventDefault()
  setActiveModuleTab(nextIndex)
  const tabs = (event.currentTarget as HTMLElement)
    .parentElement
    ?.querySelectorAll<HTMLElement>('[role="tab"]')
  tabs?.[nextIndex]?.focus()
}
</script>

<style scoped>
.features-section {
  padding: 3rem 0 1rem;
  background: var(--bg-canvas);
}

.features-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
}

.features-and-modules {
  font-size: clamp(1.5rem, 4vw + 0.5rem, 3rem);
  font-weight: 700;
  line-height: 1.1;
  color: var(--text-primary);
  text-align: center;
  margin-bottom: 40px;
  padding: 0 1rem;
  font-family: var(--font-family-body);
}

.features-tablist {
  display: flex;
  gap: 8px;
  background: var(--bg-surface);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-xl);
  padding: 8px;
  justify-content: center;
  margin-bottom: 40px;
  width: fit-content;
  margin-left: auto;
  margin-right: auto;
}

.features-tablist .component-tab {
  min-height: var(--btn-height-primary);
  padding: var(--space-3) var(--space-5);
  border-radius: var(--radius-lg);
  cursor: pointer;
  transition: all 0.2s ease;
}

.features-tablist .component-tab.active {
  background: var(--accent);
}

.features-tablist .component-tab.active .tab-text {
  color: var(--on-accent);
}

.tab-text {
  font-size: var(--font-size-md);
  color: var(--accent);
}

.features-tabpanel {
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 60px;
  align-items: start;
}

.section-title-text {
  font-size: var(--font-size-xl);
  color: var(--text-primary);
  margin-bottom: 24px;
}

.tool-icon {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(var(--accent-rgb), 0.1);
  border-radius: var(--radius-md);
  flex-shrink: 0;
  color: var(--accent);
}

.features-tools .features-tool {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  min-height: var(--btn-height-primary);
  gap: var(--space-3);
  padding: var(--space-2);
  border-radius: var(--radius-md);
  cursor: pointer;
  text-align: left;
  text-transform: none;
  transition: all 0.2s ease;
  margin-bottom: var(--space-3);
}

.features-tools .features-tool:hover {
  background: var(--bg-surface);
}

.tool-icon .v-icon {
  opacity: 0.9;
}

.tool-text {
  color: var(--text-body);
}

.module-preview-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: var(--bg-surface);
  border-radius: var(--radius-xl);
  border: 1px solid var(--border-subtle);
  overflow: hidden;
}

.preview-single {
  width: 100%;
  position: relative;
  aspect-ratio: 16 / 10;
}

.preview-single .module-preview-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.preview-carousel {
  width: 100%;
  position: relative;
  aspect-ratio: 16 / 10;
}

.preview-carousel .module-preview-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.module-preview-image {
  max-width: 100%;
  width: 100%;
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-card);
}

/* Fade transition for carousel */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Carousel indicators */
.carousel-indicators {
  display: flex;
  gap: 8px;
  margin-top: 16px;
  justify-content: center;
}

.indicator-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: var(--border-subtle);
  cursor: pointer;
  transition: all 0.3s ease;
}

.indicator-dot.active {
  background: var(--accent);
  transform: scale(1.2);
}

.indicator-dot:hover {
  background: var(--accent);
  opacity: 0.7;
}

.preview-placeholder {
  text-align: center;
  color: var(--text-body);
}

.placeholder-icon {
  font-size: var(--font-size-display);
  margin-bottom: 16px;
}

.placeholder-subtitle {
  font-size: var(--font-size-sm);
  color: var(--text-muted);
}

@media (max-width: 767px) {
  .features-section {
    padding: 3rem 0;
  }

  .features-tablist {
    flex-wrap: nowrap;
    width: calc(100% - 2rem);
    margin: 0 1rem 40px;
    padding: 4px;
    gap: 0;
    justify-content: space-evenly;
  }

  .features-tablist .component-tab {
    flex: 1;
    text-align: center;
    padding: var(--space-2) var(--space-1);
  }

  .tab-text {
    font-size: var(--font-size-caption);
  }

  .features-tabpanel {
    grid-template-columns: 1fr;
    gap: 40px;
  }
}
</style>
